import { useState } from "react";
import { PoundSterling } from "lucide-react";

export default function PriceRating({ value, onChange, label, max = 5 }) {
  const [hoverValue, setHoverValue] = useState(0);
  
  const displayValue = hoverValue || value || 0;
  
  return (
    <div className="w-full">
      {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
      <div 
        className="flex items-center gap-0.5 flex-wrap"
        onMouseLeave={() => setHoverValue(0)}
      >
        {[1, 2, 3, 4, 5].map((i) => (
          <button
            key={i}
            type="button"
            onClick={() => onChange(i)}
            onMouseEnter={() => setHoverValue(i)}
            className={`p-1 sm:p-1.5 rounded-lg transition-all duration-150 ${
              i <= displayValue
                ? "text-green-600 bg-green-50 scale-110"
                : "text-gray-300 hover:text-green-400 hover:bg-green-50/50"
            }`}
            data-testid={`price-rating-${i}`}
          >
            <PoundSterling className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        ))}
      </div>
      <div className="mt-1 text-xs text-gray-400">
        {value === 0 || !value ? "Tap to select" : null}
        {value === 1 && "£ - Very affordable"}
        {value === 2 && "££ - Budget friendly"}
        {value === 3 && "£££ - Moderate"}
        {value === 4 && "££££ - Pricey"}
        {value === 5 && "£££££ - Premium"}
      </div>
    </div>
  );
}

// Display component for showing price level (read-only)
// Handles both legacy monetary values (e.g., 3.50) and new 1-5 scale (integers)
export function PriceDisplay({ value, size = "md" }) {
  // Convert value to 1-5 scale
  // New scale uses integers 1-5
  // Legacy uses decimals or values > 5
  const getScaleValue = (val) => {
    if (!val || val === 0) return 0;
    
    // If it's a whole number between 1-5, treat as new scale
    if (Number.isInteger(val) && val >= 1 && val <= 5) {
      return val;
    }
    
    // Legacy conversion for monetary values
    // Coffee: £0-2.50=1, £2.51-3.50=2, £3.51-4.50=3, £4.51-5.50=4, £5.50+=5
    // Food: £0-5=1, £5.01-8=2, £8.01-12=3, £12.01-18=4, £18+=5
    // Using a simple heuristic based on coffee prices as the baseline
    if (val <= 2.50) return 1;
    if (val <= 3.50) return 2;
    if (val <= 4.50) return 3;
    if (val <= 5.50) return 4;
    if (val <= 8) return 3;      // Food range
    if (val <= 12) return 4;     // Food range
    return 5;
  };
  
  const displayValue = Math.min(Math.max(getScaleValue(value), 0), 5);
  
  const sizeClasses = {
    sm: "w-3.5 h-3.5",
    md: "w-4 h-4",
    lg: "w-5 h-5"
  };
  
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <PoundSterling
          key={i}
          className={`${sizeClasses[size]} ${
            i <= displayValue ? "text-green-600" : "text-gray-200"
          }`}
        />
      ))}
    </div>
  );
}
